import "../../Leistungen.css";
import "./Gehoerschutz.css";
import { gehoerschutz } from "../../interfaces/gehoerschutz";
import { Helmet } from "react-helmet-async";
import { useRef, useEffect, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import HeroSectionLeistungen from "../../components/HeroSectionLeistungen";

const blockColors = ["#eb721f", "#038cb2", "#e6f3fb", "#398ccb"];

// LazyVideo Komponente mit mehreren Quellen
const LazyVideo = ({ sources }: { sources: { src: string; type: string }[] }) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 }
		);

		if (videoRef.current) {
			observer.observe(videoRef.current);
		}

		return () => observer.disconnect();
	}, []);

	return (
		<video
			ref={videoRef}
			autoPlay={isVisible}
			loop
			muted
			playsInline
		>
			{isVisible && sources.map((source, index) => (
				<source key={index} src={source.src} type={source.type} />
			))}
		</video>
	);
};

export default function Gehoerschutz() {
	const gehoerschutzGridRef = useRef<HTMLElement>(null);
	const moreInfoSectionRef = useRef<HTMLElement>(null);

	useEffect(() => {
		// ScrollTrigger registrieren
		gsap.registerPlugin(ScrollTrigger);

		// Animationen für Gehörschutz-Boxen
		if (gehoerschutzGridRef.current) {
			const gehoerschutzBoxes = gehoerschutzGridRef.current.querySelectorAll('.gehoerschutz-box') as NodeListOf<HTMLElement>;

			gehoerschutzBoxes.forEach((box: HTMLElement, index: number) => {
				// Alle Boxen von unten animieren
				gsap.fromTo(box,
					{ opacity: 0, y: 100 },
					{
						opacity: 1,
						y: 0,
						duration: 1.2,
						ease: "power2.out",
						scrollTrigger: {
							trigger: box,
							start: "top 85%",
							end: "bottom 15%",
							toggleActions: "play none none none",
							once: true
						}
					}
				);
			});
		}

		// Animation für "Gut zu wissen" Block - von links
		if (moreInfoSectionRef.current) {
			gsap.fromTo(moreInfoSectionRef.current,
				{ opacity: 0, x: -100 },
				{
					opacity: 1,
					x: 0,
					duration: 1.2,
					ease: "power2.out",
					scrollTrigger: {
						trigger: moreInfoSectionRef.current,
						start: "top 80%",
						end: "bottom 20%",
						toggleActions: "play none none none",
						once: true
					}
				}
			);

			// Animation für DGUV-Logo (CSS-Pseudo-Element) - von unten
			gsap.fromTo(moreInfoSectionRef.current,
				{
					opacity: 0,
					x: -100,
					"--dguv-y": "-50px"
				} as any,
				{
					opacity: 1,
					x: 0,
					"--dguv-y": "0px",
					duration: 1.2,
					ease: "power2.out",
					scrollTrigger: {
						trigger: moreInfoSectionRef.current,
						start: "top 80%",
						end: "bottom 20%",
						toggleActions: "play none none none",
						once: true
					}
				} as any
			);
		}

		// Cleanup function
		return () => {
			ScrollTrigger.getAll().forEach(trigger => trigger.kill());
		};
	}, []);

	return (
		<main>
			<Helmet>
				<link rel="canonical" href="https://hoergut-buehlot.de/Gehoerschutz" />
			</Helmet>
			<div className="container">
				<HeroSectionLeistungen
					title={"Geh&ouml;rschutz"}
					subtitle={"Individuelle L&ouml;sungen f&uuml;r Arbeit und Freizeit"}
					image={{ src: "../../img/sowei-unlimited-weiss.webp", alt: "Gehörschutz Duo" }}
					sectionClassName="gehoerschutz-hero-section"
				/>
			</div>

			<section className="gehoerschutz-grid container" ref={gehoerschutzGridRef}>
				{gehoerschutz.map((item, idx) => {
					const baseColor = blockColors[idx % blockColors.length];
					const style = { background: baseColor };
					return (
						<div key={idx} className="gehoerschutz-box" style={style}>
							<div className="gehoerschutz-content">
								<div className="gehoerschutz-image">
									<img src={item.url} alt={item.description} />
								</div>
								<div className="gehoerschutz-text">
									<p>{item.text}</p>
								</div>
							</div>
						</div>
					);
				})}
			</section>

			<div className="container">
				<section className="image-video-section">
					<div className="image-video-image">
						<LazyVideo
							sources={[
								{ src: "../../videos/marketing-video-gehoerschutz-gehoerluchs.mp4", type: "video/mp4" },
								{ src: "../../videos/marketing-video-gehoerschutz-gehoerluchs.webm", type: "video/webm" }
							]}
						/>
					</div>
				</section>
			</div>

			<section className="container">
				<article className="more-info-section" ref={moreInfoSectionRef}>
					<h2>Gut zu wissen:</h2>
					<p>
						In vielen Fällen kann dieser individuelle Gehörschutz über die Berufsgenossenschaft (BG) abgerechnet werden, sodass dem Mitarbeiter selbst keine oder nur geringe Kosten entstehen.
					</p>
				</article>
			</section>
		</main>
	)
}
